/** Browser-safe contracts. No configuration reads, timers, I/O or enrollment on import. */
export const CART_RECOVERY_SOURCE_ENABLED = true;
export const RECOVERY_ELIGIBILITY_POLICY_VERSION = "account-cart-email-eligibility-v1";
export type RecoveryLine = { variantId: string; quantity: number };
export type RecoveryMutation = {
  initiatingUserId: string | null;
  initiatingIdentityRevision?: number;
  terminalRevision?: number;
  eligibilityGeneration: string | null;
  cartId: string; country: string | null; lines: RecoveryLine[]; kind: "mutation" | "empty";
};
export type RecoveryEligibilitySnapshot = {
  owner: string | null; enabled: boolean; eligible: boolean; eligibilityGeneration: string | null;
  known: boolean; reading: boolean; error: boolean;
};
type EligibilityTicket = { owner: string; revision: number };
/** Eligibility is a server policy lease, never evidence of customer consent.
 * Tickets fence identity transitions, terminal operations and late asynchronous replies. */
export function createRecoveryEligibilityState() {
  let revision = 0;
  let state: RecoveryEligibilitySnapshot = { owner: null, enabled: false, eligible: false,
    eligibilityGeneration: null, known: false, reading: false, error: false };
  const listeners = new Set<() => void>();
  const update = (next: Partial<RecoveryEligibilitySnapshot>) => {
    state = { ...state, ...next }; listeners.forEach((listener) => listener());
  };
  const current = (ticket: EligibilityTicket, owner: unknown) =>
    ticket.owner === state.owner && owner === ticket.owner && ticket.revision === revision;
  return {
    getSnapshot: () => state,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    setOwner(owner: string | null) {
      if (owner === state.owner) return;
      revision++;
      update({ owner, enabled: false, eligible: false, eligibilityGeneration: null,
        known: false, reading: false, error: false });
    },
    generation(owner: string | null) {
      return owner && owner === state.owner && state.known && state.eligible
        ? state.eligibilityGeneration : null;
    },
    capture(owner: string | null): EligibilityTicket | null {
      return owner && owner === state.owner ? { owner, revision } : null;
    },
    beginRead(owner: string): EligibilityTicket | null {
      if (owner !== state.owner || state.known || state.reading) return null;
      const ticket = { owner, revision: ++revision };
      update({ reading: true, error: false }); return ticket;
    },
    finishRead(ticket: EligibilityTicket, result: { ownerId: string; enabled: boolean;
      eligible: boolean; eligibilityGeneration: string | null; retryable?: boolean }) {
      if (!current(ticket, result.ownerId)) return false;
      const eligible = result.enabled && result.eligible && typeof result.eligibilityGeneration === "string";
      update({ enabled: result.enabled, eligible, eligibilityGeneration: eligible ? result.eligibilityGeneration : null,
        known: true, reading: false, error: result.retryable === true }); return true;
    },
    fail(ticket: EligibilityTicket) {
      if (!current(ticket, ticket.owner)) return;
      update({ reading: false, known: true, eligible: false, eligibilityGeneration: null, error: true });
    },
    invalidate(owner: string | null): EligibilityTicket | null {
      if (!owner || owner !== state.owner) return null;
      revision++;
      update({ eligible: false, eligibilityGeneration: null, known: true, reading: false, error: false });
      return { owner, revision };
    },
    /** One later genuine action may retry failed bootstrap; its own captured receipt is never replayed. */
    retryAfterActivity(owner: string | null) {
      if (!owner || owner !== state.owner || !state.error || state.reading) return false;
      revision++;
      update({ known: false, error: false }); return true;
    },
    /** Only a durable terminal acknowledgment can reopen bootstrap; never changes old receipts. */
    afterAcknowledgment(ticket: EligibilityTicket | null) {
      if (!ticket || !current(ticket, ticket.owner)) return false;
      revision++;
      update({ known: false, reading: false, eligible: false, eligibilityGeneration: null });
      return true;
    },
  };
}
export const recoveryEligibilityState = createRecoveryEligibilityState();
export function getRecoveryEligibilityGeneration(userId: string | null): string | null {
  return recoveryEligibilityState.generation(userId);
}
export function recoveryIdentityMatches(initiatingUserId: string | null, currentUserId: string | null): boolean {
  return initiatingUserId !== null && initiatingUserId === currentUserId;
}
export function recoveryFingerprint(country: string | null, lines: RecoveryLine[]): string {
  const quantities = new Map<string, number>();
  for (const line of lines) quantities.set(line.variantId, (quantities.get(line.variantId) ?? 0) + line.quantity);
  return JSON.stringify([country, [...quantities].sort(([a], [b]) => a.localeCompare(b))]);
}
export function recoveryGate(config: {
  enabled?: boolean; cutoff?: string | null; checkoutVerified?: boolean; purchaseVerified?: boolean;
} | null, now: number): boolean {
  if (!config?.enabled || !config.checkoutVerified || !config.purchaseVerified || !config.cutoff) return false;
  const cutoff = Date.parse(config.cutoff);
  return Number.isFinite(cutoff) && cutoff < now;
}
export function verifiedRecoveryEmail(user: {
  id?: string; email?: string; email_confirmed_at?: string | null; is_anonymous?: boolean;
} | null): string | null {
  if (!user?.id || user.is_anonymous || !user.email_confirmed_at || !user.email) return null;
  return user.email.trim().toLowerCase();
}
export function recoveryStepDue(activity: string, delayDays: number, now: number): boolean {
  return Number.isFinite(delayDays) && delayDays >= 0 && Number.isFinite(Date.parse(activity)) &&
    now >= Date.parse(activity) + delayDays * 86400_000;
}
export function recoveryOutcome(result: { success: boolean; deliveryUncertain?: boolean }): "sent" | "failed" | "unknown" {
  return result.success ? "sent" : result.deliveryUncertain ? "unknown" : "failed";
}
// Explicit successful user mutations only. Snapshot/visibility/restore never subscribe as activity.
// Each bus captures its gate once; isolated test buses cannot enable production dispatch.
export function createRecoveryMutationBus(enabled: boolean) {
  const listeners = new Set<(event: RecoveryMutation) => void>();
  return {
    onRecoveryMutation(listener: (event: RecoveryMutation) => void): () => void {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    emitRecoveryMutation(event: RecoveryMutation): void {
      if (!enabled) return;
      for (const listener of listeners) {
        try { listener({ ...event, lines: event.lines.map((line) => ({ ...line })) }); } catch { /* isolated */ }
      }
    },
  };
}
const recoveryMutationBus = createRecoveryMutationBus(CART_RECOVERY_SOURCE_ENABLED);
export function onRecoveryMutation(listener: (event: RecoveryMutation) => void): () => void {
  return recoveryMutationBus.onRecoveryMutation(listener);
}
export function emitRecoveryMutation(event: RecoveryMutation): void {
  if (CART_RECOVERY_SOURCE_ENABLED && event.kind === "empty") {
    const ticket = recoveryEligibilityState.invalidate(event.initiatingUserId);
    event = { ...event, terminalRevision: ticket?.revision };
  }
  recoveryMutationBus.emitRecoveryMutation(event);
}